import { useEffect, useState } from "react";
import { ChevronUp, Send, Star, Vote, MessageSquare, CheckCircle2, ClipboardList, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { getDeviceId } from "@/lib/device";
import { useSSE } from "@/lib/useSSE";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const device = getDeviceId();
const votedKey = "pitso_qvotes";
const getVoted = () => JSON.parse(localStorage.getItem(votedKey) || "{}");
const setVotedId = (id, on) => { const v = getVoted(); if (on) v[id] = 1; else delete v[id]; localStorage.setItem(votedKey, JSON.stringify(v)); };

export function QuestionsPanel({ eventId, sessionId }) {
  const [questions, setQuestions] = useState([]);
  const [voted, setVoted] = useState(getVoted());
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [anon, setAnon] = useState(true);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = () => {
    const q = sessionId ? `?session_id=${sessionId}` : "";
    api.get(`/attendee/event/${eventId}/questions${q}`).then(({ data }) => setQuestions(data));
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [eventId, sessionId]);
  useSSE(`/stream/event/${eventId}`, (m) => { if (m.type === "questions") load(); });

  const openAsk = () => { setText(""); setSubmitted(false); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/attendee/event/${eventId}/questions`, { text, session_id: sessionId || null, is_anonymous: anon, author_name: anon ? null : "Guest" });
      setSubmitted(true);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const upvote = async (qid) => {
    const { data } = await api.post(`/attendee/questions/${qid}/upvote`, { device_id: device });
    setVotedId(qid, data.voted); setVoted(getVoted());
    setQuestions((prev) => prev.map((q) => (q.id === qid ? data : q)).sort((a, b) => (b.pinned - a.pinned) || (b.upvotes - a.upvotes)));
  };

  return (
    <div className="pb-4">
      {/* Primary action */}
      <button onClick={openAsk} data-testid="attendee-ask-open"
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground font-semibold text-base h-14 shadow-sm active:scale-[0.99] transition-transform">
        <Plus className="h-5 w-5" /> Ask a Question
      </button>

      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-6 mb-2">Live questions</p>

      {questions.length === 0 ? (
        <div className="text-center py-10 text-sm text-muted-foreground"><MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-40" /> No questions yet — be the first to ask.</div>
      ) : (
        <div className="space-y-2" data-testid="attendee-questions-list">
          {questions.map((q) => {
            const mine = !!voted[q.id];
            return (
              <div key={q.id} className={`rounded-xl bg-card border p-3 flex items-start gap-3 ${q.pinned ? "border-accent" : "border-border"}`}>
                <button onClick={() => upvote(q.id)} data-testid={`attendee-upvote-${q.id}`} className={`flex flex-col items-center rounded-lg border px-2.5 py-2 min-w-[3rem] transition-colors ${mine ? "border-accent bg-accent/10 text-accent" : "border-border text-foreground"}`}>
                  <ChevronUp className="h-5 w-5" /><span className="font-bold text-sm">{q.upvotes}</span>
                </button>
                <div className="flex-1">
                  {q.pinned && <span className="text-[10px] font-bold text-accent">PINNED</span>}
                  <p className="text-[15px] text-foreground leading-snug">{q.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">{q.is_anonymous ? "Anonymous" : q.author_name}{q.status === "answered" && <span className="ml-2 text-accent font-medium">· Answered</span>}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="attendee-ask-dialog">
          {submitted ? (
            <div className="text-center py-6" data-testid="attendee-ask-success">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-accent" />
              <h3 className="font-display text-xl font-bold text-foreground">Question submitted</h3>
              <p className="text-sm text-muted-foreground mt-1.5">Your question has been sent to the moderator.</p>
              <Button onClick={() => setOpen(false)} className="mt-6 w-full h-12 rounded-xl bg-primary text-primary-foreground text-base" data-testid="attendee-ask-back">Back to session</Button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="attendee-ask-form">
              <h3 className="font-display text-xl font-bold text-foreground mb-3">Ask a Question</h3>
              <Textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Type your question…" className="text-base resize-none" data-testid="attendee-question-input" />
              <div className="flex items-center gap-2 mt-3">
                <Switch id="anon" checked={anon} onCheckedChange={setAnon} data-testid="attendee-anon-toggle" />
                <Label htmlFor="anon" className="text-sm text-muted-foreground">Submit anonymously</Label>
              </div>
              <Button type="submit" disabled={sending || !text.trim()} className="mt-4 w-full h-12 rounded-xl bg-accent text-accent-foreground text-base" data-testid="attendee-submit-question">
                <Send className="h-4 w-4 mr-1.5" /> {sending ? "Sending…" : "Submit Question"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function PollsPanel({ eventId, sessionId }) {
  const [polls, setPolls] = useState([]);
  const [voted, setVoted] = useState(() => JSON.parse(localStorage.getItem("pitso_pvotes") || "{}"));
  const [picks, setPicks] = useState({});

  const load = () => {
    const q = sessionId ? `?session_id=${sessionId}` : "";
    api.get(`/attendee/event/${eventId}/polls${q}`).then(({ data }) => setPolls(data));
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [eventId, sessionId]);
  useSSE(`/stream/event/${eventId}`, (m) => { if (m.type === "polls") load(); });

  const togglePick = (pollId, optId, multiple) => {
    setPicks((prev) => {
      const cur = prev[pollId] || [];
      if (multiple) return { ...prev, [pollId]: cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId] };
      return { ...prev, [pollId]: [optId] };
    });
  };

  const vote = async (poll) => {
    const chosen = picks[poll.id] || [];
    if (!chosen.length) return toast.error("Select an option");
    try {
      const { data } = await api.post(`/attendee/polls/${poll.id}/vote`, { option_ids: chosen, device_id: device });
      const nv = { ...voted, [poll.id]: chosen }; setVoted(nv); localStorage.setItem("pitso_pvotes", JSON.stringify(nv));
      setPolls((prev) => prev.map((p) => (p.id === poll.id ? data : p)));
    } catch (err) {
      if (err.response?.status === 400) { const nv = { ...voted, [poll.id]: chosen }; setVoted(nv); localStorage.setItem("pitso_pvotes", JSON.stringify(nv)); }
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (polls.length === 0) return <div className="text-center py-10 text-sm text-muted-foreground"><Vote className="h-6 w-6 mx-auto mb-2 opacity-40" /> No active polls right now.</div>;

  return (
    <div className="space-y-4 pb-4" data-testid="attendee-polls">
      {polls.map((p) => {
        const total = p.options.reduce((a, o) => a + o.votes, 0) || 1;
        const hasVoted = !!voted[p.id];
        const myPicks = picks[p.id] || [];
        return (
          <div key={p.id} className="rounded-xl bg-card border border-border p-4">
            <h3 className="font-display font-bold text-foreground text-base">{p.question}</h3>
            <p className="text-[11px] text-muted-foreground mb-3 capitalize">{p.type} choice</p>
            <div className="space-y-2">
              {p.options.map((o) => {
                const pct = Math.round((o.votes / total) * 100);
                const selected = myPicks.includes(o.id);
                return (
                  <button key={o.id} onClick={() => !hasVoted && togglePick(p.id, o.id, p.type === "multiple")} disabled={hasVoted} data-testid={`attendee-poll-option-${o.id}`}
                    className={`relative w-full text-left rounded-lg border overflow-hidden px-3 py-3 text-[15px] transition-colors ${selected || voted[p.id]?.includes(o.id) ? "border-accent" : "border-border"} ${hasVoted ? "" : "hover:border-accent"}`}>
                    {hasVoted && <span className="absolute inset-y-0 left-0 bg-accent/10" style={{ width: `${pct}%` }} />}
                    <span className="relative flex justify-between"><span className="text-foreground">{o.text}</span>{hasVoted && <span className="text-muted-foreground font-medium">{pct}%</span>}</span>
                  </button>
                );
              })}
            </div>
            {!hasVoted && <Button onClick={() => vote(p)} className="mt-3 w-full h-11 rounded-xl bg-accent text-accent-foreground" data-testid={`attendee-poll-submit-${p.id}`}>Submit vote</Button>}
          </div>
        );
      })}
    </div>
  );
}

function RatingRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-[15px] text-foreground">{label}</span>
      <div className="flex gap-1.5">{[1, 2, 3, 4, 5].map((s) => <button key={s} type="button" onClick={() => onChange(s)} data-testid={`${testid}-${s}`}><Star className={`h-7 w-7 ${s <= value ? "fill-accent text-accent" : "text-border"}`} /></button>)}</div>
    </div>
  );
}

export function FeedbackPanel({ eventId, sessionId }) {
  const [overall, setOverall] = useState(0);
  const [speaker, setSpeaker] = useState(0);
  const [content, setContent] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!overall) return toast.error("Please give an overall rating");
    try {
      await api.post(`/attendee/event/${eventId}/feedback`, { overall_rating: overall, speaker_rating: speaker || null, content_rating: content || null, comment, session_id: sessionId || null });
      setDone(true); toast.success("Thanks for your feedback!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (done) return <div className="text-center py-10"><CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-accent" /><p className="font-display text-lg font-bold text-foreground">Thank you!</p><p className="text-sm text-muted-foreground mt-1">Your feedback helps improve future events.</p></div>;

  return (
    <div className="rounded-xl bg-card border border-border p-5 space-y-1" data-testid="attendee-rate-panel">
      <p className="font-display text-lg font-bold text-foreground mb-2">Rate {sessionId ? "this session" : "the event"}</p>
      <RatingRow label="Overall" value={overall} onChange={setOverall} testid="attendee-overall" />
      <RatingRow label="Speaker" value={speaker} onChange={setSpeaker} testid="attendee-speaker" />
      <RatingRow label="Content relevance" value={content} onChange={setContent} testid="attendee-content" />
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Any comments? (optional)" className="mt-3 text-base" data-testid="attendee-comment" />
      <Button onClick={submit} className="w-full rounded-xl bg-accent text-accent-foreground h-12 mt-3 text-base" data-testid="attendee-submit-rating">Submit feedback</Button>
    </div>
  );
}

export function SurveyPanel({ eventId }) {
  const [surveys, setSurveys] = useState([]);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState({});

  useEffect(() => { api.get(`/attendee/event/${eventId}/surveys`).then(({ data }) => setSurveys(data)); }, [eventId]);

  const setAns = (sid, qid, val) => setAnswers((p) => ({ ...p, [sid]: { ...(p[sid] || {}), [qid]: val } }));
  const submit = async (survey) => {
    try { await api.post(`/attendee/surveys/${survey.id}/respond`, { answers: answers[survey.id] || {} }); setDone((p) => ({ ...p, [survey.id]: true })); toast.success("Survey submitted"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (surveys.length === 0) return <div className="text-center py-10 text-sm text-muted-foreground"><ClipboardList className="h-6 w-6 mx-auto mb-2 opacity-40" /> No surveys available yet.</div>;

  return (
    <div className="space-y-4 pb-4" data-testid="attendee-surveys">
      {surveys.map((s) => done[s.id] ? (
        <div key={s.id} className="rounded-xl bg-card border border-border p-6 text-center"><CheckCircle2 className="h-7 w-7 mx-auto mb-2 text-accent" /><p className="font-display font-bold text-foreground">Survey completed</p></div>
      ) : (
        <div key={s.id} className="rounded-xl bg-card border border-border p-4 space-y-4">
          <h3 className="font-display font-bold text-foreground">{s.title}</h3>
          {s.questions.map((q) => (
            <div key={q.id}>
              <p className="text-sm text-foreground mb-2">{q.text}</p>
              {q.type === "rating" && <div className="flex gap-1.5">{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setAns(s.id, q.id, n)}><Star className={`h-7 w-7 ${n <= (answers[s.id]?.[q.id] || 0) ? "fill-accent text-accent" : "text-border"}`} /></button>)}</div>}
              {(q.type === "multiple_choice" || q.type === "yes_no") && (
                <div className="flex flex-wrap gap-2">{(q.options || []).map((o) => <button key={o} type="button" onClick={() => setAns(s.id, q.id, o)} className={`px-3 py-2 rounded-full text-sm border ${answers[s.id]?.[q.id] === o ? "border-accent bg-accent/10 text-accent" : "border-border text-foreground"}`}>{o}</button>)}</div>
              )}
              {q.type === "open_text" && <Textarea rows={2} value={answers[s.id]?.[q.id] || ""} onChange={(e) => setAns(s.id, q.id, e.target.value)} placeholder="Your answer" />}
            </div>
          ))}
          <Button onClick={() => submit(s)} className="w-full rounded-xl bg-accent text-accent-foreground h-11" data-testid={`attendee-survey-submit-${s.id}`}>Submit survey</Button>
        </div>
      ))}
    </div>
  );
}
